import {
  Component,
  OnInit,
  ViewChild,
  ElementRef,
  NgZone,
} from '@angular/core';
import { MapsAPILoader, MouseEvent } from '@agm/core';
import { GameSettingsService } from '../game-settings.service';

interface IUnit {
  label: string;
  value: string;
  conversionFactor: number;
  zoom: number;
}

@Component({
  selector: 'app-location',
  templateUrl: './location.component.html',
  styleUrls: ['./location.component.scss'],
})
export class LocationComponent implements OnInit {
  @ViewChild('search') public searchElementRef: ElementRef;

  // radius slider bounds (in meters)
  minRadiusMeters = 1;
  maxRadiusMeters = 3000;

  units: IUnit[] = [
    { label: 'Kilometers', value: 'kilometers', conversionFactor: 1000, zoom: 3 },
    { label: 'Meters',      value: 'meters',      conversionFactor: 1,    zoom: 13 },
    { label: 'Miles',       value: 'miles',       conversionFactor: 1609.34, zoom: 3 },
    { label: 'Feet',        value: 'feet',        conversionFactor: 0.3048, zoom: 15 },
  ];

  private geoCoder: any;

  constructor(
    private mapsAPILoader: MapsAPILoader,
    private ngZone: NgZone,
    public gameSettingsService: GameSettingsService
  ) {}

  ngOnInit() {
    this.mapsAPILoader.load().then(() => {
      this.setCurrentLocation();
      this.geoCoder = new google.maps.Geocoder();

      const autocomplete = new google.maps.places.Autocomplete(
        this.searchElementRef.nativeElement
      );
      autocomplete.addListener('place_changed', () => {
        this.ngZone.run(() => {
          const place: google.maps.places.PlaceResult = autocomplete.getPlace();
          if (!place.geometry) return;

          this.gameSettingsService.latitude  = place.geometry.location.lat();
          this.gameSettingsService.longitude = place.geometry.location.lng();
        });
      });
    });
  }

  private setCurrentLocation() {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(position => {
        if (
          !this.gameSettingsService.latitude &&
          !this.gameSettingsService.longitude
        ) {
          this.gameSettingsService.latitude  = position.coords.latitude;
          this.gameSettingsService.longitude = position.coords.longitude;
        }
      });
    }
  }

  markerDragEnd(event: MouseEvent) {
    this.gameSettingsService.latitude  = event.coords.lat;
    this.gameSettingsService.longitude = event.coords.lng;
    this.getAddress(event.coords.lat, event.coords.lng);
  }

  getAddress(lat: number, lng: number) {
    this.geoCoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === 'OK' && results[0]) {
        this.gameSettingsService.latitude  = results[0].geometry.location.lat();
        this.gameSettingsService.longitude = results[0].geometry.location.lng();
      }
    });
  }

  onSelectChange(event: any) {
    this.gameSettingsService.unit = event.value;
  }

  // instead of event: any or {value:number}, do:
  onRadiusInput(value: number) {
    this.gameSettingsService.radius = value;
    // no need to cache; calculateRadius() is used in the template
  }

  calculateRadius(): number {
    const unit = this.units.find(u => u.value === this.gameSettingsService.unit);
    const factor = unit ? unit.conversionFactor : 1;
    return Math.floor(this.gameSettingsService.radius * factor);
  }

  getZoomValue(): number {
    const unit = this.units.find(u => u.value === this.gameSettingsService.unit);
    return unit ? unit.zoom : 13;
  }

  /** Format thumb label, e.g. “500 m” */
  formatRadiusLabel(value: number) {
    return `${value} m`;
  }
}
